import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteUserApi,
  fetchUsersApi,
  updateUserApi,
  resetUserPasswordApi,
  fetchInstructorsApi,
  deleteInstructorApi,
  updateInstructorApi,
  resetInstructorPasswordApi,
  createInstructorApi,
  uploadClassImagesApi,
  deleteClassImageApi,
  uploadTypeImageApi,
  deleteTypeImageApi,
  uploadSubtypeImageApi,
  deleteSubtypeImageApi,
  setClassInProgressOfCancellationApi,
  reactivateClassApi,
  getClassReportApi,
  getInstructorClassHoursReportApi,
  getEnrollmentsReportApi,
  archiveClassApi,
  unarchiveClassApi,
  getArchivedClassApi,
  fetchArchivedClassesApi,
  createPriceApi,
  updatePriceApi,
  deletePriceApi,
  fetchPricesApi,
  fetchPaymentHistoryApi,
  fetchPaymentStatisticsApi,
  fetchAttendeesApi,
  fetchAttendeesForUserApi,
} from "../../utils/api/admin";
import {
  Class,
  Location,
  Schedule,
  Type,
  Attendee,
  Instructor,
  Price,
} from "../types/types";
import {
  createLocationApi,
  deleteLocationApi,
  fetchLocationsApi,
  updateLocationApi,
} from "../../utils/api/locations";
import {
  createTypeApi,
  deleteTypeApi,
  fetchTypesApi,
  updateTypeApi,
} from "../../utils/api/types";
import {
  createClassApi,
  deleteClassApi,
  fetchClassesApi,
  fetchClassesListApi,
  fetchClassesForAttendanceApi,
  fetchClassesDropdownApi,
  getClassApi,
  updateClassApi,
} from "../../utils/api/classes";
import {
  bulkCreateSchedulesApi,
  bulkDeleteSchedulesApi,
  bulkUpdateSchedulesApi,
  cancelScheduleApi,
  cancelScheduleDeactivationApi,
  createScheduleApi,
  deactivateScheduleApi,
  deleteScheduleApi,
  deleteScheduleCancellationApi,
  fetchSchedulesApi,
  recreateScheduleApi,
  updateScheduleApi,
} from "../../utils/api/schedules";
import {
  createAttendeeApi,
  deleteAttendeeApi,
  getAttendeesToScheduleApi,
  updateAttendeeApi,
  adjustAttendeeClassesApi,
} from "../../utils/api/attendees";

interface FetchUsersParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchUsers = createAsyncThunk<
  { users: any[]; total: number },
  FetchUsersParams
>("admin/fetchUsers", async (params) => {
  const response = await fetchUsersApi(params);
  return response.data;
});

export const deleteUser = createAsyncThunk<number, number>(
  "admin/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await deleteUserApi(userId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateUser = createAsyncThunk<
  any,
  { userId: number; userData: any }
>("admin/updateUser", async ({ userId, userData }, { rejectWithValue }) => {
  try {
    const response = await updateUserApi(userId, userData);
    return response.data;
  } catch (err: any) {
    if (err.response && err.response.data && err.response.data.message) {
      return rejectWithValue(err.response.data.message);
    }
    return rejectWithValue(err.message || "Failed to update user.");
  }
});

export const resetUserPassword = createAsyncThunk<
  void,
  { userId: number; language: string }
>(
  "admin/resetUserPassword",
  async ({ userId, language }, { rejectWithValue }) => {
    try {
      await resetUserPasswordApi(userId, language);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchInstructorsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchInstructors = createAsyncThunk<
  { instructors: any[]; total: number },
  FetchInstructorsParams
>("admin/fetchInstructors", async (params) => {
  const response = await fetchInstructorsApi(params);
  return response.data;
});

export const deleteInstructor = createAsyncThunk<number, number>(
  "admin/deleteInstructor",
  async (instructorId, { rejectWithValue }) => {
    try {
      await deleteInstructorApi(instructorId);
      return instructorId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateInstructor = createAsyncThunk<
  any,
  { instructorId: number; instructorData: any }
>(
  "admin/updateInstructor",
  async ({ instructorId, instructorData }, { rejectWithValue }) => {
    try {
      const response = await updateInstructorApi(instructorId, instructorData);
      return response.data;
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      }
      return rejectWithValue(err.message || "Failed to update instructor.");
    }
  }
);

export const resetInstructorPassword = createAsyncThunk<
  void,
  { instructorId: number; language: string }
>(
  "admin/resetInstructorPassword",
  async ({ instructorId, language }, { rejectWithValue }) => {
    try {
      await resetInstructorPasswordApi(instructorId, language);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createInstructor = createAsyncThunk<
  any,
  { instructorData: Omit<Instructor, "id" | "role">; language: string }
>(
  "admin/createInstructor",
  async ({ instructorData, language }, { rejectWithValue }) => {
    try {
      const response = await createInstructorApi(
        {
          ...instructorData,
          isInstructor: true,
        },
        language
      );
      return response.data;
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      }
      return rejectWithValue(err.message || "Failed to update instructor.");
    }
  }
);

interface FetchLocationsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchLocations = createAsyncThunk<
  { locations: Location[]; total: number },
  FetchLocationsParams
>("admin/fetchLocations", async (params) => {
  const response = await fetchLocationsApi(params);
  return response.data;
});

export const deleteLocation = createAsyncThunk<number, number>(
  "admin/deleteLocation",
  async (locationId, { rejectWithValue }) => {
    try {
      await deleteLocationApi(locationId);
      return locationId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateLocation = createAsyncThunk<
  any,
  { locationId: number; locationData: any }
>(
  "admin/updateLocation",
  async ({ locationId, locationData }, { rejectWithValue }) => {
    try {
      const response = await updateLocationApi(locationId, locationData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createLocation = createAsyncThunk<any, Omit<Location, "id">>(
  "admin/createLocation",
  async (locationData, { rejectWithValue }) => {
    try {
      const response = await createLocationApi(locationData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchTypesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchTypes = createAsyncThunk<
  { types: Type[]; total: number },
  FetchTypesParams
>("admin/fetchTypes", async (params) => {
  const response = await fetchTypesApi(params);
  return response.data;
});

export const deleteType = createAsyncThunk<number, number>(
  "admin/deleteType",
  async (typeId, { rejectWithValue }) => {
    try {
      await deleteTypeApi(typeId);
      return typeId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateType = createAsyncThunk<
  any,
  { typeId: number; typeData: any }
>("admin/updateType", async ({ typeId, typeData }, { rejectWithValue }) => {
  try {
    const response = await updateTypeApi(typeId, typeData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const createType = createAsyncThunk<any, Omit<Type, "id">>(
  "admin/createType",
  async (typeData, { rejectWithValue }) => {
    try {
      const response = await createTypeApi(typeData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchClassesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
  startDate?: string;
  endDate?: string;
}

export const fetchClasses = createAsyncThunk<
  { classes: Class[]; total: number },
  FetchClassesParams
>("admin/fetchClasses", async (params) => {
  const response = await fetchClassesApi(params);
  return response.data;
});

interface FetchClassesListParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchClassesList = createAsyncThunk<
  { classes: Class[]; total: number },
  FetchClassesListParams
>("admin/fetchClassesList", async (params) => {
  const response = await fetchClassesListApi(params);
  return response.data;
});

interface FetchClassesForAttendanceParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
  startDate?: string;
  endDate?: string;
}

export const fetchClassesForAttendance = createAsyncThunk<
  { classes: Class[]; total: number },
  FetchClassesForAttendanceParams
>("admin/fetchClassesForAttendance", async (params) => {
  const response = await fetchClassesForAttendanceApi(params);
  return response.data;
});

export const fetchClassesDropdown = createAsyncThunk<
  { id: number; title: any }[]
>("admin/fetchClassesDropdown", async () => {
  const response = await fetchClassesDropdownApi();
  return response.data;
});

export const deleteClass = createAsyncThunk<number, number>(
  "admin/deleteClass",
  async (classId, { rejectWithValue }) => {
    try {
      await deleteClassApi(classId);
      return classId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateClass = createAsyncThunk<
  any,
  { classId: number; classData: any }
>("admin/updateClass", async ({ classId, classData }, { rejectWithValue }) => {
  try {
    const response = await updateClassApi(classId, classData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const createClass = createAsyncThunk<
  any,
  Omit<Class, "id" | "instructors"> & {
    instructors: Instructor[];
  }
>("admin/createClass", async (classData, { rejectWithValue }) => {
  try {
    const response = await createClassApi(classData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchClass = createAsyncThunk<Class, number>(
  "admin/fetchClass",
  async (classId, { rejectWithValue }) => {
    try {
      const response = await getClassApi(classId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchSchedulesParams {
  classId: number;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export const fetchSchedules = createAsyncThunk<
  { schedules: Schedule[]; total: number },
  FetchSchedulesParams
>("admin/fetchSchedules", async (params) => {
  const response = await fetchSchedulesApi(params);
  return response.data;
});

export const deleteSchedule = createAsyncThunk<number, number>(
  "admin/deleteSchedule",
  async (scheduleId, { rejectWithValue }) => {
    try {
      await deleteScheduleApi(scheduleId);
      return scheduleId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSchedule = createAsyncThunk<Schedule, Omit<Schedule, "id">>(
  "admin/createSchedule",
  async (scheduleData, { rejectWithValue }) => {
    try {
      const response = await createScheduleApi(scheduleData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSchedule = createAsyncThunk<
  Schedule,
  { scheduleId: number; scheduleData: Partial<Schedule> }
>(
  "admin/updateSchedule",
  async ({ scheduleId, scheduleData }, { rejectWithValue }) => {
    try {
      const response = await updateScheduleApi(scheduleId, scheduleData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const bulkCreateSchedules = createAsyncThunk<
  Schedule[],
  Omit<Schedule, "id">[]
>("admin/bulkCreateSchedules", async (schedulesData, { rejectWithValue }) => {
  try {
    const response = await bulkCreateSchedulesApi(schedulesData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const bulkUpdateSchedules = createAsyncThunk<
  Schedule[],
  Partial<Schedule>[]
>("admin/bulkUpdateSchedules", async (schedulesData, { rejectWithValue }) => {
  try {
    const response = await bulkUpdateSchedulesApi(schedulesData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const bulkDeleteSchedules = createAsyncThunk<number[], number[]>(
  "admin/bulkDeleteSchedules",
  async (scheduleIds, { rejectWithValue }) => {
    try {
      await bulkDeleteSchedulesApi(scheduleIds);
      return scheduleIds;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cancelSchedule = createAsyncThunk<
  any,
  {
    scheduleId: number;
    date: Date;
    comment: string;
    endDate?: Date;
  }
>("admin/cancelSchedule", async (data, { rejectWithValue }) => {
  try {
    return await cancelScheduleApi(data);
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const deleteScheduleCancellation = createAsyncThunk<any, number>(
  "admin/deleteScheduleCancellation",
  async (id, { rejectWithValue }) => {
    try {
      return await deleteScheduleCancellationApi(id);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchAttendeesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
}

export const fetchAttendees = createAsyncThunk<
  { attendees: Attendee[]; total: number },
  FetchAttendeesParams
>("admin/fetchAttendees", async (params) => {
  const response = await fetchAttendeesApi(params);
  return response.data;
});

export const fetchAttendeesForUser = createAsyncThunk<Attendee[], number>(
  "admin/fetchAttendeesForUser",
  async (userId) => {
    const response = await fetchAttendeesForUserApi(userId);
    return response.data;
  }
);

export const deleteAttendee = createAsyncThunk<number, number>(
  "admin/deleteAttendee",
  async (attendeeId) => {
    await deleteAttendeeApi(attendeeId);
    return attendeeId;
  }
);

export const updateAttendee = createAsyncThunk<
  any,
  { attendeeId: number; attendeeData: any }
>("admin/updateAttendee", async ({ attendeeId, attendeeData }) => {
  const response = await updateAttendeeApi(attendeeId, attendeeData);
  return response.data;
});

export const adjustAttendeeClasses = createAsyncThunk<
  any,
  {
    attendeeId: number;
    adjustmentData: {
      regularClasses?: number;
      specialClasses?: Record<string, number>;
      reason?: string;
    };
  }
>("admin/adjustAttendeeClasses", async ({ attendeeId, adjustmentData }) => {
  const response = await adjustAttendeeClassesApi(attendeeId, adjustmentData);
  return response.data;
});

export const createAttendee = createAsyncThunk<any, Omit<Attendee, "id">>(
  "admin/createAttendee",
  async (attendeeData) => {
    const response = await createAttendeeApi(attendeeData);
    return response.data;
  }
);

export const getAttendeesToSchedule = createAsyncThunk<Attendee[], number>(
  "admin/getAttendeesToSchedule",
  async (scheduleId) => {
    const response = await getAttendeesToScheduleApi(scheduleId);
    return response.data;
  }
);

export const uploadClassImages = createAsyncThunk<
  { message: string; imageUrls: string[]; classId: number },
  { classId: number; images: File[] }
>(
  "admin/uploadClassImages",
  async ({ classId, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((image) => {
        formData.append("images", image);
      });

      const response = await uploadClassImagesApi(classId, formData);
      return { ...response.data, classId };
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteClassImage = createAsyncThunk<
  { message: string; classId: number; imageUrl: string },
  { classId: number; imageUrl: string }
>("admin/deleteClassImage", async ({ classId, imageUrl }) => {
  const response = await deleteClassImageApi(classId, imageUrl);
  return { ...response.data, classId, imageUrl };
});

export const uploadTypeImage = createAsyncThunk<
  { message: string; imageUrl: string; typeId: number },
  { typeId: number; image: File }
>(
  "admin/uploadTypeImage",
  async ({ typeId, image }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", image);

      const response = await uploadTypeImageApi(typeId, formData);
      return { ...response.data, typeId };
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTypeImage = createAsyncThunk<
  { message: string; typeId: number; imageUrl: string },
  { typeId: number; imageUrl: string }
>("admin/deleteTypeImage", async ({ typeId, imageUrl }) => {
  const response = await deleteTypeImageApi(typeId, imageUrl);
  return { ...response.data, typeId, imageUrl };
});

export const uploadSubtypeImage = createAsyncThunk<
  { message: string; imageUrl: string; subtypeId: number },
  { subtypeId: number; image: File }
>(
  "admin/uploadSubtypeImage",
  async ({ subtypeId, image }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", image);

      const response = await uploadSubtypeImageApi(subtypeId, formData);
      return { ...response.data, subtypeId };
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSubtypeImage = createAsyncThunk<
  { message: string; subtypeId: number; imageUrl: string },
  { subtypeId: number; imageUrl: string }
>("admin/deleteSubtypeImage", async ({ subtypeId, imageUrl }) => {
  const response = await deleteSubtypeImageApi(subtypeId, imageUrl);
  return { ...response.data, subtypeId, imageUrl };
});

export const setClassInProgressOfCancellation = createAsyncThunk<
  Class,
  { classId: number; cancelDate: string; language: string }
>(
  "admin/setClassInProgressOfCancellation",
  async ({ classId, cancelDate, language }) => {
    const response = await setClassInProgressOfCancellationApi(
      classId,
      cancelDate,
      language
    );
    return response.data;
  }
);

export const reactivateClass = createAsyncThunk<Class, number>(
  "admin/reactivateClass",
  async (classId, { rejectWithValue }) => {
    try {
      const response = await reactivateClassApi(classId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getClassReport = createAsyncThunk<
  any,
  {
    instructorId?: number;
    classId?: number;
    fromDate?: string;
    toDate?: string;
    typeId?: number;
    locationId?: number;
  }
>("reports/getClassReport", async (params, { rejectWithValue }) => {
  try {
    const response = await getClassReportApi(params);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const getInstructorClassHoursReport = createAsyncThunk<
  any,
  {
    instructorId?: string;
    classId?: string;
    typeId?: string;
    fromDate?: string;
    toDate?: string;
  }
>(
  "reports/getInstructorClassHoursReport",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getInstructorClassHoursReportApi(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getEnrollmentsReport = createAsyncThunk<
  any,
  {
    instructorId?: string;
    classId?: string;
    fromDate?: string;
    toDate?: string;
    typeId?: string;
    locationId?: string;
  }
>("reports/getEnrollmentsReport", async (params, { rejectWithValue }) => {
  try {
    const response = await getEnrollmentsReportApi(params);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const deactivateSchedule = createAsyncThunk<
  Schedule,
  { scheduleId: number; deactivationDate: string }
>(
  "admin/deactivateSchedule",
  async ({ scheduleId, deactivationDate }, { rejectWithValue }) => {
    try {
      const response = await deactivateScheduleApi(
        scheduleId,
        deactivationDate
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cancelScheduleDeactivation = createAsyncThunk<Schedule, number>(
  "admin/cancelScheduleDeactivation",
  async (scheduleId, { rejectWithValue }) => {
    try {
      const response = await cancelScheduleDeactivationApi(scheduleId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const recreateSchedule = createAsyncThunk<Schedule, number>(
  "admin/recreateSchedule",
  async (scheduleId, { rejectWithValue }) => {
    try {
      const response = await recreateScheduleApi(scheduleId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const archiveClass = createAsyncThunk<
  Class,
  { classId: number; language?: string }
>("admin/archiveClass", async ({ classId, language }, { rejectWithValue }) => {
  try {
    const response = await archiveClassApi(classId, language);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const unarchiveClass = createAsyncThunk<
  Class,
  { classId: number; language?: string }
>(
  "admin/unarchiveClass",
  async ({ classId, language }, { rejectWithValue }) => {
    try {
      const response = await unarchiveClassApi(classId, language);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchArchivedClasses = createAsyncThunk<
  { classes: Class[]; total: number },
  FetchClassesParams
>("admin/fetchArchivedClasses", async (params) => {
  const response = await fetchArchivedClassesApi(params);
  return response.data;
});

export const getArchivedClass = createAsyncThunk<Class, number>(
  "admin/getArchivedClass",
  async (classId, { rejectWithValue }) => {
    try {
      const response = await getArchivedClassApi(classId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createPrice = createAsyncThunk<
  any,
  {
    amount: number;
    numberOfClasses: number;
    specialType?: string | null;
    currency: string;
    classId?: number | null;
    isAdminOnly?: boolean;
  }
>("admin/createPrice", async (data, { rejectWithValue }) => {
  try {
    const response = await createPriceApi(data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchPrices = createAsyncThunk<
  { prices: Price[]; total: number },
  string | undefined
>("admin/fetchPrices", async (searchTerm?: string) => {
  const response = await fetchPricesApi(searchTerm);
  // Backend returns array directly, so we need to format it correctly
  const prices = response.data.map((price: any) => ({
    ...price,
    createdAt: new Date(price.createdAt),
    updatedAt: new Date(price.updatedAt),
  }));
  return {
    prices: prices,
    total: prices.length,
  };
});

export const updatePrice = createAsyncThunk<
  any,
  {
    id: number;
    data: Partial<{
      amount: number;
      numberOfClasses?: number | null;
      specialType?: string | null;
      currency: string;
      classId?: number | null;
      isAdminOnly?: boolean;
    }>;
  }
>("admin/updatePrice", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updatePriceApi(id, data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const deletePrice = createAsyncThunk<number, number>(
  "admin/deletePrice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePriceApi(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

interface FetchPaymentHistoryParams {
  page: number;
  limit: number;
  attendeeId?: number;
  classId?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const fetchPaymentHistory = createAsyncThunk<
  {
    payments: any[];
    total: number;
    manualPaymentsCount: number;
    stripePaymentsCount: number;
  },
  FetchPaymentHistoryParams
>("admin/fetchPaymentHistory", async (params) => {
  const response = await fetchPaymentHistoryApi(params);
  return response.data;
});

interface FetchPaymentStatisticsParams {
  startDate?: string;
  endDate?: string;
  attendeeId?: number;
}

export const fetchPaymentStatistics = createAsyncThunk<
  {
    totalAmount: number;
    totalPayments: number;
    successfulPayments: number;
    manualPayments: { count: number; amount: number };
    stripePayments: { totalCount: number; count: number; amount: number };
  },
  FetchPaymentStatisticsParams
>("admin/fetchPaymentStatistics", async (params) => {
  const response = await fetchPaymentStatisticsApi(params);
  return response.data;
});
